'use client';

import { type ReactNode, useEffect, useState } from 'react';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AccessibilityPanel from './components/accessibility/AccessibilityPanel';
import PrivacyNoticePanel from './components/privacy/PrivacyNoticePanel';
import PersistentMusicBar from './components/ui/PersistentMusicBar';
import { useGlobalMusic } from './context/GlobalMusicContext';
import { useI18n } from './context/LanguageContext';
import { detectPathLanguage, stripLanguagePrefix } from './i18n/urlLanguage';
import { Link, useLocation } from './router/nextCompat';
import { getNewsArticleSeo, type NewsArticleSeoLanguage } from './data/newsArticleSeo';

const SEO_BY_ROUTE: Array<{
  test: (pathname: string) => boolean;
  title: string;
  description: string;
}> = [
  {
    test: (pathname) => pathname === '/',
    title: 'Skoletoon Universe | Mode, contenu et experiences',
    description:
      "Mode, contenu shoppable, abonnement premium et experiences transmedia dans l'univers Skoletoon.",
  },
  {
    test: (pathname) => pathname.startsWith('/stores') || pathname.startsWith('/shop'),
    title: 'Stores | Skoletoon Universe',
    description:
      'Decouvre les collections, boutiques et sorties a venir de l ecosysteme Skoletoon.',
  },
  {
    test: (pathname) => pathname.startsWith('/subscription') || pathname.startsWith('/abonnement'),
    title: 'Subscription | Skoletoon Universe',
    description: 'Rejoins l abonnement premium Skoletoon et debloque des experiences exclusives.',
  },
  {
    test: (pathname) => pathname.startsWith('/music') || pathname.startsWith('/news'),
    title: 'Music & News | Skoletoon Universe',
    description: "Suis les actualites et la scene musicale de l'univers Skoletoon.",
  },
];

function setOrCreateMeta(nameOrProperty: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${nameOrProperty}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(nameOrProperty, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOrCreateCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function NotFoundContent() {
  const { language } = useI18n();
  const isEn = language === 'en';

  return (
    <div className="pt-32 text-center px-6 py-32">
      <h1 className="display-text text-7xl text-gradient mb-4">404</h1>
      <p className="text-xl text-white/60 mb-8">
        {isEn
          ? 'This page does not exist in the Skoleom universe.'
          : "Cette page n'existe pas dans l'univers Skoleom."}
      </p>
      <Link to="/" className="btn-skoleom inline-block">
        {isEn ? 'Back to home' : "Retour a l'accueil"}
      </Link>
    </div>
  );
}

export default function AppFrame({ children }: { children: ReactNode }) {
  const { language } = useI18n();
  const location = useLocation();
  const isEnglishRoute = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const normalizedPathname = stripLanguagePrefix(location.pathname);
  const { hasTrack } = useGlobalMusic();
  const [persistentMusicBarDismissed, setPersistentMusicBarDismissed] = useState(false);
  const canControlMusicOnPage =
    normalizedPathname === '/' ||
    normalizedPathname.startsWith('/music') ||
    normalizedPathname.startsWith('/business');
  const isVisualPage =
    normalizedPathname === '/' ||
    normalizedPathname.startsWith('/stores') ||
    normalizedPathname.startsWith('/hub');
  const showPersistentMusicBar = hasTrack && canControlMusicOnPage && !persistentMusicBarDismissed;

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      if (!id) return;
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: 'start' });
      });
      return;
    }

    if (normalizedPathname.startsWith('/stores')) {
      const params = new URLSearchParams(location.search);
      if (params.has('brand') || params.has('category')) return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash, normalizedPathname]);

  useEffect(() => {
    const locale = language === 'en' ? 'en_US' : 'fr_FR';
    document.documentElement.lang = language;
    setOrCreateMeta('property', 'og:locale', locale);

    const entry = SEO_BY_ROUTE.find((item) => item.test(normalizedPathname));
    const newsSegments = normalizedPathname.split('/').filter(Boolean);
    const routeLanguage = detectPathLanguage(location.pathname) ?? 'fr';
    const articleSeo =
      newsSegments[0] === 'news' && newsSegments[1] && newsSegments[1] !== 'external'
        ? getNewsArticleSeo(newsSegments[1], routeLanguage as NewsArticleSeoLanguage)
        : null;
    const title = articleSeo?.title ?? entry?.title ?? 'Skoletoon Universe';
    const description =
      articleSeo?.description ??
      entry?.description ??
      "Mode, contenu shoppable, abonnement premium et experiences transmedia dans l'univers Skoletoon.";
    const canonical = `${window.location.origin}${location.pathname}`;

    document.title = title;
    setOrCreateMeta('name', 'description', description);
    setOrCreateMeta('property', 'og:title', title);
    setOrCreateMeta('property', 'og:description', description);
    setOrCreateMeta('property', 'og:url', canonical);
    setOrCreateMeta('name', 'twitter:title', title);
    setOrCreateMeta('name', 'twitter:description', description);
    setOrCreateCanonical(canonical);
  }, [language, location.pathname, normalizedPathname]);

  useEffect(() => {
    if (!hasTrack) {
      setPersistentMusicBarDismissed(false);
    }
  }, [hasTrack]);

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-skoleom-lime focus:px-4 focus:py-2 focus:text-xs focus:font-bold focus:uppercase focus:tracking-wide focus:text-black"
      >
        {isEnglishRoute ? 'Skip to main content' : 'Aller au contenu principal'}
      </a>
      <Header />
      <AccessibilityPanel />
      <main
        id="main-content"
        tabIndex={-1}
        className={`${!showPersistentMusicBar ? 'flex-1' : ''} ${showPersistentMusicBar ? 'pb-24' : ''} ${isVisualPage ? 'a11y-reading-exempt' : ''}`.trim()}
      >
        {children}
      </main>
      {showPersistentMusicBar ? (
        <PersistentMusicBar onClose={() => setPersistentMusicBarDismissed(true)} />
      ) : null}
      <Footer />
      <PrivacyNoticePanel />
    </div>
  );
}
